/**
 * Protocol response full download.
 *
 * It "simulates" opening the protocolres screen: it calls the very same
 * endpoints that `ProtocolRes` and its subcomponents consume
 * (see src/Dashboard/ProtocolRes/index.tsx, DomainCompositeResults.tsx and
 * ScreeningResults.tsx) and dumps everything into a single JSON file.
 *
 * A JSON is used instead of a CSV because the data is hierarchical
 * (protocolres -> protocol item -> feature -> score) and holds rich values
 * such as the word counts and the semaphore probabilities per population,
 * which cannot be represented in a flat table without losing information.
 *
 * The participant audio responses are not included: the backend signs the
 * audio urls with a 5 minutes expiration, so the link would be useless by the
  * time the file is read. The audio is still downloadable from the protocolres
 * screen.
 *
 * The population distribution histograms are not included either: they are the
 * heaviest part of the file (an {x: [], y: []} pair per score, per feature, per
 * item) and they are reference curves rather than participant data. They are
 * still plotted in the protocolres screen.
 */

import moment from 'moment';
import { saveAs } from 'file-saver';

import Apirest from '../../Content/Apirest';
import { getContentStatic } from '../../Content/cms';
import { store } from '../../store';

// Import interfaces
import {
    FeatureExtractionResData,
    ProtocolItemResData,
    ProtocolResData
} from '../Protocol/types';


/** Server responses that have no interface declared yet */

interface SemaphoreProbabilityData {
    population: string
    score: string
    value: number
}

interface SemaphoreFunctionData {
    lambda_function: string // the lambda function entry
    bhattacharyya_probabilities: SemaphoreProbabilityData[]
}

// The semaphore probabilities come grouped by protocol item id
type SemaphoreData = {[protocol_item_id: string]: SemaphoreFunctionData[]};

interface DomainCompositeScoreData {
    domain: string
    score?: number
    show?: boolean
}

interface ScreeningScoreData {
    screening: string
    score?: number
    show?: boolean
}

type ResultsResponse<T> = T[] | {results: T[]};


/** Helpers */

/**
 * Wraps an Apirest call in a promise.
 * Apirest resolves through a success callback and returns the underlying
 * jQuery promise, which is used here to reject when the request fails.
 */
function apiGet<T>(call: (success: (response: T) => void) => any): Promise<T> {
    return new Promise<T>((resolve, reject) => {
        let request = call(resolve);

        if (request !== undefined && typeof request.fail === 'function') {
            request.fail(() => reject(new Error('apirest_request_failed')));
        }
    });
}

/** Returns the CMS text of a key, or undefined if the key is not translated */
function cmsText(key: string): string | undefined {
    let text = getContentStatic(key);
    return (text === key || text === '') ? undefined : text;
}

function resultsOf<T>(response: ResultsResponse<T>): T[] {
    return Array.isArray(response) ? response : (response?.results || []);
}


/** Data mapping */

function score2json(
    fer: FeatureExtractionResData,
    score: string,
    value: number,
    protocolItemRes: ProtocolItemResData,
    semaphoreData: SemaphoreData
) {
    // Same key the summary table uses to name a score row
    // (see src/Dashboard/ProtocolRes/FeatureExtraction.tsx)
    let featureName = fer.lambda_function.name + '__' + score;

    // Bhattacharyya probabilities of this score, one per population
    // (same lookup as findAllScoreValues in src/Dashboard/ProtocolRes/index.tsx)
    let semaphore = (semaphoreData[protocolItemRes.protocol_item.id] || [])
        .find(entry => entry.lambda_function === fer.lambda_function.entry)
        ?.bhattacharyya_probabilities
        .filter(probability => probability.score === score)
        .map(probability => ({
            population: probability.population,
            value: probability.value
        })) || [];

    return {
        score: score,
        display_name: cmsText(
            'protocol_result__feature_extraction__' + featureName + '__name'
        ) || featureName,
        explanation: cmsText(
            'protocol_result__feature_extraction__' + featureName +
            '__short_explanation'
        ),
        value: value,
        semaphore: semaphore
    };
}

function protocolItemRes2json(
    protocolItemRes: ProtocolItemResData,
    semaphoreData: SemaphoreData
) {
    let transcription = protocolItemRes.feature_extraction_res.find(
        fer => fer.lambda_function.entry === 'transcript'
    );

    let wordcloud = protocolItemRes.feature_extraction_res.find(
        fer => fer.lambda_function.entry === 'words-count'
    );

    return {
        protocol_item_res: {
            id: protocolItemRes.id,
            date_created: protocolItemRes.date_created,
            is_skipped: protocolItemRes.is_skipped,
            skipped_purpose: protocolItemRes.skipped_purpose
        },
        // The backend strips the empty fields of the protocol item, so any of
        // them can be missing (see ProtocolItemSerializer.to_representation)
        protocol_item: protocolItemRes.protocol_item,
        transcript: transcription?.data?.transcript,
        words_count: wordcloud?.data?.words_count,
        // Every feature is exported, including the ones the screen hides
        // (show === false) or that are not ready yet. The flags are kept so
        // the consumer can filter them.
        features: protocolItemRes.feature_extraction_res.map(fer => ({
            lambda_function: fer.lambda_function,
            status: fer.status,
            show: fer.show,
            data: fer.data,
            scores: Object.entries(fer.scores || {}).map(
                ([score, value]) => score2json(
                    fer,
                    score,
                    value,
                    protocolItemRes,
                    semaphoreData
                )
            )
        }))
    };
}


/** Main entry point */

export async function downloadProtocolResJson(
    participant_id: string,
    protocolres_id: string
) {
    let protocolRes = await apiGet<ProtocolResData>(
        success => Apirest.get_protocolres(participant_id, protocolres_id, success)
    );

    // Same order the protocolres screen shows the items in
    let responses = [...protocolRes.responses].sort(
        (a, b) => a.protocol_item.order - b.protocol_item.order
    );

    // The optional sections are fetched in parallel and each one falls back to
    // an empty value, so a single failing section does not abort the download
    let [semaphoreData, domainComposites, screenings] =
        await Promise.all([
            apiGet<SemaphoreData>(
                success => Apirest.get_protocol_res_bhattacharyya_probabilities(
                    protocolres_id, success
                )
            ).catch(() => ({} as SemaphoreData)),

            apiGet<ResultsResponse<DomainCompositeScoreData>>(
                success => Apirest.get_protocol_res_domain_composite_scores(
                    protocolres_id, success
                )
            ).catch(() => [] as DomainCompositeScoreData[]),

            apiGet<ResultsResponse<ScreeningScoreData>>(
                success => Apirest.get_protocol_res_screening_scores(
                    protocolres_id, success
                )
            ).catch(() => [] as ScreeningScoreData[])
        ]);

    let state = store.getState();
    let participant = state.participants.find(
        p => p.id.toString() === participant_id
    );
    let examiner = state.examiner;

    let payload = {
        export: {
            version: 2,
            generated_at: moment().toISOString(),
            source: 'voice-frontend'
        },
        participant: participant,
        examiner: examiner && {
            id: examiner.id,
            first_name: examiner.first_name,
            last_name: examiner.last_name
        },
        protocol_res: {
            id: protocolRes.id,
            date_created: protocolRes.date_created,
            is_completed: protocolRes.is_completed,
            active: protocolRes.active,
            comment: protocolRes.comment
        },
        protocol: protocolRes.protocol,
        domain_composite_scores: resultsOf(domainComposites).map(item => ({
            domain: item.domain,
            display_name: cmsText(
                'protocol_result__composite__domains__' + item.domain + '__name'
            ) || item.domain,
            score: item.score,
            show: item.show,
            explanation: cmsText(
                'protocol_result__composite__domains__' + item.domain +
                '__explanation'
            )
        })),
        screening_scores: resultsOf(screenings).map(item => ({
            screening: item.screening,
            display_name: cmsText(
                'protocol_result__screening__' + item.screening + '__name'
            ) || item.screening,
            score: item.score,
            show: item.show,
            explanation: cmsText(
                'protocol_result__screening__' + item.screening + '__explanation'
            )
        })),
        items: responses.map(
            protocolItemRes => protocolItemRes2json(
                protocolItemRes,
                semaphoreData
            )
        )
    };

    let filename = (
        'protocolres_' + protocolres_id + '_' +
        (participant?.code || participant_id) + '_' +
        moment(protocolRes.date_created).format('YYYY-MM-DD_HH-mm-ss') +
        '.json'
    );

    saveAs(
        new Blob(
            [JSON.stringify(payload, null, 2)],
            {type: 'application/json;charset=utf-8;'}
        ),
        filename
    );
}
